<template>
  <div class="cards-dashboard">
    <!-- Header section -->
    <!-- Header section with improved search placement -->
    <!-- Header section with improved search placement -->
    <base-header class="dashboard-header pb-6 pt-8 bg-gradient">
      <b-container fluid class="header-container">
        <div class="header-body">
          <div class="row align-items-center">
            <div class="col-lg-6">
              <div class="title-container text-left">
                <h1 class="header-title">{{ $t('cards.title') }}</h1>
              </div>
            </div>
            <!-- Search moved to the right column for better balance -->
            <div class="col-lg-6">
              <!-- Search container with improved styling and positioning -->
              <div class="search-container">
                <div class="input-group search-input-group">
                  <div class="input-group-prepend">
      <span class="input-group-text">
        <i class="ni ni-zoom-split-in"></i>
      </span>
                  </div>
                  <input
                    type="text"
                    class="form-control search-input"
                    :placeholder="$t('cards.searchPlaceholder')"
                    v-model="searchQuery"
                    @input="performSearch"
                    @focus="searchActive = true"
                    @blur="handleSearchBlur"
                  >
                  <div class="input-group-append" v-if="searchQuery">
                    <button class="btn btn-outline-secondary clear-btn" type="button" @click="clearSearch">
                      <i class="ni ni-fat-remove"></i>
                    </button>
                  </div>
                </div>

                <!-- Improved Search Results Dropdown -->
                <div class="search-results-container" v-if="showSearchResults && searchActive">
                  <div class="search-results">
                    <div
                      v-for="result in searchResults"
                      :key="result.id + result.type"
                      class="search-result-item"
                      @click="selectResult(result)"
                      @mousedown.prevent
                    >
                      <div class="card-icon">
                        <i class="ni" :class="result.type === 'receiving' ? 'ni-money-coins' : 'ni-send'"></i>
                      </div>
                      <div class="card-info">
                        <div class="card-number">{{ result.display }}</div>
                        <div v-if="result.type === 'sending'" class="card-note">
                          {{ $t('cards.searchByVisible') }}
                        </div>
                      </div>
                    </div>

                    <div v-if="searchResults.length === 0 && searchQuery.length >= 4" class="no-results">
                      {{ $t('cards.notFound') }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Alert messages -->
        <transition name="fade">
          <div v-if="alert_message_success" class="alert alert-success mt-4" role="alert">
            <i class="ni ni-check-bold mr-2"></i>
            {{ alert_message_success }}
          </div>
        </transition>

        <transition name="fade">
          <div v-if="alert_message_error" class="alert alert-danger mt-4" role="alert">
            <i class="ni ni-notification-70 mr-2"></i>
            {{ alert_message_error }}
          </div>
        </transition>
      </b-container>
    </base-header>

    <!-- Main content section -->
    <b-container fluid class="mt--6">
      <b-row>
        <b-col>
          <div class="content-block">
            <div class="row mx-auto">
              <!-- Receiving cards section -->
              <div class="col-lg-6 mb-4">
                <div class="section-header">
                  <h2 class="section-title">
                    <i class="ni ni-money-coins mr-2"></i>
                    {{ $t('cards.receivingCards') }}
                  </h2>
                </div>

                <!-- Add card button -->
                <div class="add-card-btn-wrapper mb-4">
                  <button type="button" class="btn btn-add-card" @click="dialogVisible = true">
                    <i class="ni ni-fat-add mr-2"></i>
                    <span>{{ $t('cards.addCard') }}</span>
                  </button>
                </div>

                <!-- Cards list with animation -->
                <transition-group name="card-list" tag="div" class="cards-container">
                  <RCreditCard
                    class="mb-4"
                    v-for="card in RCards"
                    :key="card.id"
                    :card="card"
                    @limit_edited="fetchRCards"
                    @card_removed="fetchRCards"
                  />
                </transition-group>
              </div>

              <!-- Sending cards section -->
              <div class="col-lg-6 mb-4">
                <div class="section-header">
                  <h2 class="section-title">
                    <i class="ni ni-send mr-2"></i>
                    {{ $t('cards.p2pCards') }}
                  </h2>
                </div>

                <!-- Add card button -->
                <div class="add-card-btn-wrapper mb-4">
                  <button type="button" class="btn btn-add-card" @click="dialogVisibleSend = true">
                    <i class="ni ni-fat-add mr-2"></i>
                    <span>{{ $t('cards.addP2PCard') }}</span>
                  </button>
                </div>

                <!-- Cards list with animation -->
                <transition-group name="card-list" tag="div" class="cards-container">
                  <SCreditCard
                    class="mb-4"
                    v-for="card in SCards"
                    :key="card.id"
                    :card="card"
                    :can_remove="providers.find(item => item.is_card_remove && item.type === card.type)"
                    @card_removed="fetchSCards"
                  />
                </transition-group>
              </div>
            </div>
          </div>
        </b-col>
      </b-row>
    </b-container>

    <!-- Add Receiving Card Dialog -->
    <el-dialog
      title="Добавить карту для получения"
      :visible.sync="dialogVisible"
      width="420px"
      top="5vh"
      custom-class="card-dialog"
    >
      <div class="form card-form">
        <div class="form-group">
          <label for="card-number">Номер карты</label>
          <base-input
            alternative
            required
            type="number"
            inputmode="numeric"
            pattern="[0-9]*"
            min="0"
            id="card-number"
            class="card-input"
            name="card-number"
            prepend-icon="ni ni-credit-card"
            placeholder="Введите номер карты"
            v-model="cardForm.number"
          />
        </div>

        <div class="form-group">
          <div class="limit-toggle d-flex align-items-center">
            <span class="limit-toggle-label">Включить лимиты</span>
            <vs-switch class="ml-3" color="success" v-model="cardForm.is_limited"/>
          </div>
        </div>

        <div v-if="cardForm.is_limited" class="limit-settings">
          <div class="form-group">
            <label for="limit-week">Дневной лимит</label>
            <base-input
              alternative
              type="number"
              inputmode="numeric"
              pattern="[0-9]*"
              min="0"
              id="limit-week"
              class="card-input"
              prepend-icon="ni ni-calendar-grid-58"
              placeholder="Введите сумму"
              v-model="cardForm.limit_week"
            />
          </div>

          <div class="form-group">
            <label for="limit-month">Месячный лимит</label>
            <base-input
              alternative
              type="number"
              inputmode="numeric"
              pattern="[0-9]*"
              min="0"
              id="limit-month"
              class="card-input"
              prepend-icon="ni ni-calendar-grid-58"
              placeholder="Введите сумму"
              v-model="cardForm.limit_month"
            />
          </div>

          <div class="form-row">
            <div class="form-group col-md-6">
              <label for="amount-min">Мин. сумма</label>
              <base-input
                alternative
                type="number"
                inputmode="numeric"
                pattern="[0-9]*"
                min="0"
                id="amount-min"
                class="card-input"
                prepend-icon="ni ni-bold-down"
                placeholder="Мин. сумма"
                v-model="cardForm.amount_min"
              />
            </div>
            <div class="form-group col-md-6">
              <label for="amount-max">Макс. сумма</label>
              <base-input
                alternative
                type="number"
                inputmode="numeric"
                pattern="[0-9]*"
                min="0"
                id="amount-max"
                class="card-input"
                prepend-icon="ni ni-bold-up"
                placeholder="Макс. сумма"
                v-model="cardForm.amount_max"
              />
            </div>
          </div>

          <div class="form-group">
            <label for="transactions-max">Макс. транзакций (24ч)</label>
            <base-input
              alternative
              type="number"
              inputmode="numeric"
              pattern="[0-9]*"
              min="0"
              id="transactions-max"
              class="card-input"
              prepend-icon="ni ni-chart-bar-32"
              placeholder="Максимум транзакций"
              v-model="cardForm.transactions_max"
            />
          </div>
        </div>
      </div>

      <div slot="footer" class="dialog-footer">
        <base-button type="secondary" @click="closeModal">Отмена</base-button>
        <base-button
          type="primary"
          :disabled="!cardForm.number"
          @click="handleRCardAdd"
        >
          Добавить карту
        </base-button>
      </div>
    </el-dialog>

    <!-- Add P2P Card Dialog -->
    <el-dialog
      title="Добавить P2P карту"
      :visible.sync="dialogVisibleSend"
      width="420px"
      top="5vh"
      custom-class="card-dialog"
    >
      <div class="form card-form">
        <div class="form-group">
          <label for="p2p-card-number">Номер карты</label>
          <base-input
            alternative
            required
            type="number"
            inputmode="numeric"
            pattern="[0-9]*"
            min="0"
            id="p2p-card-number"
            class="card-input"
            name="card-number"
            prepend-icon="ni ni-credit-card"
            placeholder="Введите номер карты"
            v-model="SCardForm.number"
          />
        </div>

        <div class="form-row">
          <div class="form-group col-md-6">
            <label for="card-expiry">Срок действия</label>
            <base-input
              alternative
              required
              type="number"
              inputmode="numeric"
              pattern="[0-9]*"
              min="0"
              id="card-expiry"
              class="card-input"
              name="card-expiry"
              prepend-icon="ni ni-calendar-grid-58"
              placeholder="ММГГ"
              maxlength="4"
              v-model="SCardForm.expiry"
            />
          </div>
          <div class="form-group col-md-6">
            <label for="card-provider">Провайдер</label>
            <div class="provider-select-wrapper">
              <i class="ni ni-building select-icon"></i>
              <select
                id="card-provider"
                class="form-control provider-select"
                v-model="SCardForm.provider_id"
              >
                <option value="" disabled selected>Выберите провайдера</option>
                <option
                  v-for="provider in providers"
                  :key="provider.id"
                  :value="provider.id"
                >
                  {{ provider.type }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <div v-if="smsPending" class="sms-info mb-3">
          <i class="ni ni-notification-70 mr-2"></i>
          <span>{{ smsPendingMessage }}</span>
        </div>

        <div class="form-group" v-if="smsPending">
          <label for="sms-code">СМС код</label>
          <base-input
            alternative
            type="number"
            inputmode="numeric"
            pattern="[0-9]*"
            min="0"
            id="sms-code"
            class="card-input"
            name="sms-code"
            prepend-icon="ni ni-email-83"
            placeholder="Введите СМС код"
            v-model="SCardForm.sms"
          />
        </div>
      </div>

      <div slot="footer" class="dialog-footer">
        <base-button type="secondary" @click="closeModalSend">Отмена</base-button>

        <base-button
          v-if="SCardForm.number && SCardForm.expiry && SCardForm.sms"
          type="primary"
          @click="handleCardAddSMS"
        >
          Добавить карту
        </base-button>

        <base-button
          v-if="SCardForm.number && SCardForm.expiry && !SCardForm.sms && SCardForm.provider_id"
          type="primary"
          @click="handleCardAdd"
        >
          Получить СМС код
        </base-button>
      </div>
    </el-dialog>

  </div>
</template>

<style scoped lang="scss">
/* Search results styling */
.search-container {
  position: relative;
  width: 100%;
}

.search-input-group {
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  overflow: hidden;
  background-color: rgba(255, 255, 255, 0.9);
}

.search-input {
  background-color: transparent;
  border: none;
  height: 46px;
  font-size: 0.95rem;
}

.search-input:focus {
  box-shadow: none;
  background-color: #fff;
}

.input-group-text {
  background-color: transparent;
  border: none;
  padding-left: 15px;
  color: #4361EE;
}

.clear-btn {
  background: transparent;
  border: none;
  color: #606266;
  margin-right: 5px;
}

.clear-btn:hover {
  color: #ff4d4f;
}

/* Search results dropdown */
.search-results-container {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  margin-top: 5px;
  max-height: 300px;
  overflow-y: auto;
}

.search-results {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 0.5rem 0;
}

.search-result-item {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.search-result-item:hover {
  background-color: rgba(67, 97, 238, 0.05);
}

.card-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background-color: rgba(67, 97, 238, 0.1);
  border-radius: 50%;
  margin-right: 12px;
}

.card-icon i {
  color: #4361EE;
  font-size: 1rem;
}

.card-info {
  flex: 1;
}

.card-number {
  font-weight: 500;
  color: #333;
  font-size: 0.9rem;
}

.card-note {
  font-size: 0.75rem;
  color: #909399;
  margin-top: 2px;
}

.no-results {
  text-align: center;
  padding: 1rem;
  color: #909399;
  font-size: 0.9rem;
}

/* Card highlight styling */
.highlight-card {
  animation: highlight 2s ease;
}

@keyframes highlight {
  0%, 100% {
    box-shadow: none;
    transform: scale(1);
  }
  50% {
    box-shadow: 0 0 0 3px rgba(67, 97, 238, 0.5);
    transform: scale(1.02);
  }
}

// Dashboard Header
.dashboard-header {
  padding-bottom: 5rem !important;

  .title-container {
    text-align: right;
  }

  .header-title {
    color: #fff;
    font-size: 1.75rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    letter-spacing: 0.5px;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }

  .header-subtitle {
    color: rgba(255, 255, 255, 0.8);
    font-size: 1rem;
  }

  .alert {
    border: none;
    border-radius: 8px;
    font-weight: 500;

    &.alert-success {
      background-color: rgba(82, 196, 26, 0.1);
      color: #52c41a;
      border-left: 4px solid #52c41a;
    }

    &.alert-danger {
      background-color: rgba(255, 77, 79, 0.1);
      color: #ff4d4f;
      border-left: 4px solid #ff4d4f;
    }
  }
}

// Main content
.content-block {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.05);
  padding: 2rem;
  margin-bottom: 2rem;
}

// Section headers
.section-header {
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f0f0f0;

  .section-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #333;
    margin-bottom: 0.5rem;
    display: flex;
    align-items: center;

    i {
      color: #4361EE;
    }
  }

  .section-description {
    color: #888;
    font-size: 0.875rem;
    margin-bottom: 0;
  }
}

// Add Card Button
.add-card-btn-wrapper {
  display: flex;
  justify-content: flex-start;
  padding-left: 20px;
}

.btn-add-card {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 330px;
  height: 50px;
  background: linear-gradient(135deg, rgba(67, 97, 238, 0.1), rgba(58, 12, 163, 0.1));
  color: #4361EE;
  border: 2px dashed #4361EE;
  border-radius: 12px;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    background: linear-gradient(135deg, rgba(67, 97, 238, 0.2), rgba(58, 12, 163, 0.2));
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }

  i {
    font-size: 1.25rem;
  }
}

// Cards container and animations
.cards-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-left: 20px;
}

// Card list transitions
.card-list-enter-active,
.card-list-leave-active {
  transition: all 0.5s;
}

.card-list-enter,
.card-list-leave-to {
  opacity: 0;
  transform: translateY(30px);
}

.card-list-move {
  transition: transform 0.5s;
}

// Alert animations
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

// Dialog styling
.card-dialog {
  border-radius: 12px;
  overflow: hidden;

  .el-dialog__header {
    background-color: #f8f9fa;
    padding: 1rem 1.5rem;

    .el-dialog__title {
      font-weight: 600;
      color: #333;
    }
  }

  .el-dialog__body {
    padding: 1.5rem;
  }

  .el-dialog__footer {
    padding: 1rem 1.5rem;
    border-top: 1px solid #f0f0f0;
  }
}

// Card input forms
.card-form {
  .form-group {
    margin-bottom: 1.25rem;
  }

  label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #606266;
    margin-bottom: 0.5rem;
    display: block;
  }

  .card-input {
    margin-bottom: 0;
    width: 100%; /* Ensure all inputs have consistent width */
  }

  .limit-toggle {
    margin: 0.5rem 0;

    &-label {
      font-weight: 500;
      color: #606266;
    }
  }

  .limit-settings {
    background-color: #f8f9fa;
    border-radius: 8px;
    padding: 1.25rem;
    margin-top: 1rem;
    border-left: 4px solid #4361EE;
  }

  .provider-select-wrapper {
    position: relative;
    width: 100%; /* Ensure consistent width */

    .select-icon {
      position: absolute;
      left: 10px;
      top: 50%;
      transform: translateY(-50%);
      color: #8898aa;
      font-size: 1rem;
      z-index: 10;
    }

    .provider-select {
      padding-left: 2.5rem;
      height: calc(2.75rem + 2px);
      font-size: 0.875rem;
      border-radius: 0.375rem;
      transition: box-shadow 0.15s ease, border-color 0.15s ease;
      width: 100%; /* Full width */

      &:focus {
        border-color: #5e72e4;
        box-shadow: 0 0 0 0.2rem rgba(94, 114, 228, 0.25);
      }
    }
  }

  .sms-info {
    background-color: rgba(255, 193, 7, 0.1);
    border-left: 4px solid #ffc107;
    padding: 0.75rem 1rem;
    border-radius: 4px;
    font-size: 0.875rem;
    color: #856404;
    display: flex;
    align-items: center;
  }
}

// Background gradient
.bg-gradient {
  background: linear-gradient(87deg, #172b4d 0, #1a174d 100%) !important;
}

// Responsive adjustments
@media (max-width: 992px) {
  .section-header {
    text-align: left;
    padding-left: 20px;
  }

  .title-container {
    text-align: center !important;
  }

  .cards-container {
    margin-bottom: 2rem;
    align-items: center;
  }

  .btn-add-card {
    width: 90%;
    margin-bottom: 1.5rem;
  }

  .add-card-btn-wrapper {
    justify-content: center;
    padding-left: 0;
  }
}

@media (max-width: 576px) {
  .dashboard-header {
    padding-bottom: 5rem !important;

    .header-title {
      font-size: 1.5rem;
    }
  }

  .content-block {
    padding: 1.5rem 1rem;
  }

  .card-dialog {
    width: 95% !important;
    margin: 0 auto;
  }

  .form-row {
    display: flex;
    margin-right: -10px;
    margin-left: -10px;

    .form-group {
      padding-right: 10px;
      padding-left: 10px;
      flex: 0 0 50%;
      max-width: 50%;
    }
  }

  .cards-container,
  .section-header {
    padding-left: 0;
  }

  .section-header {
    text-align: center;
  }

  .btn-add-card {
    width: 100%;
  }
}
</style>

<script>
import RCreditCard from '@/components/Cards/RCreditCard.vue'
import SCreditCard from '@/components/Cards/SCreditCard.vue'
import axios from 'axios'

export default {
  name: 'UserCards',
  components: {
    RCreditCard,
    SCreditCard
  },
  data() {
    return {
      RCards: [],
      SCards: [],
      providers: [],
      dialogVisible: false,
      dialogVisibleSend: false,
      smsPending: false,
      smsPendingMessage: null,
      alert_message_success: null,
      alert_message_error: null,
      searchQuery: '',
      searchResults: [],
      showSearchResults: false,
      searchActive: false,
      cardForm: this.getDefaultCardForm(),
      SCardForm: this.getDefaultSCardForm()
    }
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    handleSearchBlur() {
      // Delay hiding results to allow click events to register
      setTimeout(() => {
        this.searchActive = false;
      }, 200);
    },

    performSearch() {
      // Clear results if query is too short
      if (!this.searchQuery || this.searchQuery.trim().length < 2) {
        this.searchResults = [];
        this.showSearchResults = false;
        return;
      }

      this.searchActive = true;

      // Clean the search query - only keep numbers
      const query = this.searchQuery.replace(/\s+/g, '').replace(/\D/g, '');

      // Search results array
      let results = [];

      // Search in P2P Cards
      if (this.SCards && this.SCards.length > 0) {
        const sendingResults = this.SCards.filter(card => {
          if (!card || !card.pan) return false;

          // IMPORTANT: Always convert to string to avoid potential type issues
          const cardNumber = String(card.pan).replace(/\s+/g, '');

          // Extract first 4 and last 4 digits
          const firstFour = cardNumber.substring(0, 4);
          const lastFour = cardNumber.substring(cardNumber.length - 4);

          return firstFour.includes(query) || lastFour.includes(query);
        }).map(card => ({
          ...card,
          type: 'sending',
          display: `P2P Карта: ${card.pan}`
        }));

        results = [...results, ...sendingResults];
      }

      // Search in Receiving Cards
      if (this.RCards && this.RCards.length > 0) {
        const receivingResults = this.RCards.filter(card => {
          if (!card || !card.number) return false;
          const cardNumber = String(card.number).replace(/\s+/g, '');
          return cardNumber.includes(query);
        }).map(card => ({
          ...card,
          type: 'receiving',
          display: `Карта для получения: ${card.number}`
        }));

        results = [...results, ...receivingResults];
      }

      // Update the state
      this.searchResults = results;
      this.showSearchResults = true; // Always show container when query is valid
    },

// Method to clear the search
    clearSearch() {
      this.searchQuery = '';
      this.searchResults = [];
      this.showSearchResults = false;
    },

// Method to select a search result and navigate to it
    selectResult(result) {
      this.showSearchResults = false;
      this.searchActive = false;

      // Find the DOM element for the selected card
      const cardType = result.type === 'receiving' ? 'r' : 's';
      const cardElement = document.querySelector(`[data-card-id="${result.id}"][data-card-type="${cardType}"]`);

      if (cardElement) {
        // Scroll to the element
        cardElement.scrollIntoView({behavior: 'smooth', block: 'center'});

        // Highlight the element temporarily
        cardElement.classList.add('highlight-card');
        setTimeout(() => {
          cardElement.classList.remove('highlight-card');
        }, 2000);
      }

      this.clearSearch();
    },
    // Get default values for card forms
    getDefaultCardForm() {
      return {
        number: null,
        is_limited: false,
        limit: null,
        limit_week: null,
        limit_month: null,
        amount_min: 0,
        amount_max: 100000000,
        transactions_max: 1000
      };
    },

    getDefaultSCardForm() {
      return {
        number: null,
        expiry: null,
        sms: null,
        provider_id: this.getDefaultProviderId() // Set Quant as default provider
      };
    },

    // Find Quant provider ID or default to first provider
    getDefaultProviderId() {
      // This will be populated after providers are loaded
      return null;
    },

    // Fetch all data at once
    async fetchData() {
      Promise.all([
        this.fetchRCards(),
        this.fetchSCards(),
        this.getProviders()
      ]).catch(error => {
        console.error('Failed to load initial data:', error);
        this.showErrorMessage('Не удалось загрузить данные');
      });
    },

    // Fetch receiving cards
    async fetchRCards() {
      try {
        const response = await axios.get('dashboard/r-cards');
        this.RCards = response.data.cards;
      } catch (error) {
        this.showErrorMessage('Не удалось загрузить карты для получения');
        console.error(error);
      }
    },

    // Fetch sending cards
    async fetchSCards() {
      try {
        const response = await axios.get('dashboard/s-cards');
        this.SCards = response.data.cards;
      } catch (error) {
        this.showErrorMessage('Не удалось загрузить P2P карты');
        console.error(error);
      }
    },

    // Get available providers
    async getProviders() {
      try {
        const response = await axios.get('dashboard/providers/card_add');
        this.providers = response.data.providers;

        // Set Quant as default provider if it exists
        if (this.providers && this.providers.length > 0) {
          const quantProvider = this.providers.find(p => p.type === 'Quant');
          if (quantProvider) {
            this.SCardForm.provider_id = quantProvider.id;
          } else {
            // Otherwise set first provider as default
            this.SCardForm.provider_id = this.providers[0].id;
          }
        }
      } catch (error) {
        this.showErrorMessage('Не удалось загрузить провайдеров');
        console.error(error);
      }
    },

    // Close add card modals
    closeModal() {
      this.dialogVisible = false;
      this.cardForm = this.getDefaultCardForm();
    },

    closeModalSend() {
      this.dialogVisibleSend = false;
      this.SCardForm = this.getDefaultSCardForm();
      this.smsPending = false;
    },

    // Show success message
    showSuccessMessage(message) {
      this.alert_message_success = message;
      setTimeout(() => {
        this.alert_message_success = null;
      }, 5000);
    },

    // Show error message
    showErrorMessage(message) {
      this.alert_message_error = message;
      setTimeout(() => {
        this.alert_message_error = null;
      }, 5000);
    },

    // Add receiving card
    async handleRCardAdd() {
      try {
        const response = await axios.post('dashboard/r-cards', {
          number: this.cardForm.number,
          limit: this.cardForm.limit,
          limit_week: this.cardForm.limit_week,
          limit_month: this.cardForm.limit_month,
          is_limited: this.cardForm.is_limited,
          amount_min: this.cardForm.amount_min,
          amount_max: this.cardForm.amount_max,
          transactions_max: this.cardForm.transactions_max
        });

        if (response.data.status === true) {
          this.fetchRCards();
          this.closeModal();
          this.showSuccessMessage('Карта успешно добавлена');
        } else {
          this.showErrorMessage('Не удалось добавить карту');
        }
      } catch (error) {
        this.showErrorMessage('Не удалось добавить карту');
        console.error(error);
      }
    },

    // Request SMS for P2P card
    async handleCardAdd() {
      try {
        const response = await axios.post('dashboard/s-cards/add', {
          number: this.SCardForm.number,
          expiry: this.SCardForm.expiry,
          provider_id: this.SCardForm.provider_id,
          code: this.SCardForm.sms,
        });

        if (response.data.status === true) {
          if (response.data.sms_pending === true) {
            this.smsPending = true;
          }
          this.smsPendingMessage = response.data.message;
        } else {
          this.closeModalSend();
          this.showErrorMessage('Не удалось запросить СМС код. Проверьте данные и попробуйте снова.');
        }
      } catch (error) {
        this.closeModalSend();
        this.showErrorMessage('Не удалось запросить СМС код');
        console.error(error);
      }
    },

    // Add P2P card with SMS verification
    async handleCardAddSMS() {
      try {
        const response = await axios.post('dashboard/s-cards/add', {
          number: this.SCardForm.number,
          expiry: this.SCardForm.expiry,
          provider_id: this.SCardForm.provider_id,
          code: this.SCardForm.sms,
        });

        if (response.data.status === true) {
          if (response.data.sms_pending === true) {
            this.smsPending = true;
            this.smsPendingMessage = response.data.message;
          } else {
            this.closeModalSend();
            this.showSuccessMessage('Карта успешно добавлена к провайдеру!');
            this.fetchSCards();
          }
        } else {
          this.closeModalSend();
          this.showErrorMessage('Не удалось добавить карту. Проверьте данные и попробуйте снова.');
        }
      } catch (error) {
        this.closeModalSend();
        this.showErrorMessage('Не удалось добавить карту');
        console.error(error);
      }
    }
  }
};
</script>
